// HTML imprimible del inventario de una empresa (funcion pura, sin dependencias del navegador).
//
// modo:
//   'completo'  listado (con caracteristicas) y luego las fichas, dos por hoja
//   'resumen'   solo el listado
//   'fichas'    solo las fichas, dos por hoja
//
// Reglas de diseno:
//  - Todas las hojas empiezan igual: el encabezado (logo + datos) se repite arriba (thead) y el
//    pie abajo (tfoot); ninguna ficha se corta entre hojas (break-inside: avoid).
//  - Un solo equipo: el encabezado lleva el logo y los datos de la empresa (infoEmpresa).
//  - Por area (area): el nombre del area va en el encabezado.
//  - Texto siempre oscuro sobre blanco; los fondos son solo apoyo (si el navegador no imprime
//    graficos de fondo, igual se lee).

const esc = v => String(v ?? '').replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

function discosTexto(raw) {
  if (!raw) return '';
  try {
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) {
      return arr.map(d => {
        const modelo = d.Modelo || d.modelo || d.model || d.Model || '';
        const tam = d.Tamaño || d.tamaño || d.size || d.Size || d.Capacidad || '';
        return [modelo, tam].filter(Boolean).join(' - ');
      }).filter(Boolean).join(' | ');
    }
    return String(arr);
  } catch (_) { return String(raw); }
}

const dinero = v => (v ? `$${Number(v).toLocaleString('es-CO')}` : '');
const fechaLarga = () => new Date().toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' });
const fechaCorta = v => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? String(v).slice(0, 10) : d.toLocaleDateString('es-CO');
};
const nombreEquipo = eq => eq.nombre_equipo || eq.identidad_valor || 'Equipo';
const estadoDe = eq => (eq.clasificacion_color === 'verde'
  ? { clave: 'v', texto: 'Al día' }
  : eq.clasificacion_color === 'magenta' ? { clave: 'm', texto: 'Crítico' }
    : eq.clasificacion_color === 'baja' ? { clave: 'b', texto: 'Dado de baja' }
      : eq.clasificacion_color === 'pendiente' ? { clave: 'p', texto: 'Pendiente' } : { clave: 'n', texto: 'Sin estado' });
const esEspecial = eq => eq.clasificacion_color === 'baja' || eq.clasificacion_color === 'pendiente';
const ultimos5 = lic => String(lic || '').slice(-5);
const cajaOffice = eq => (eq.office_caja ? `caja ${eq.office_caja}` : '');
const validada = eq => Number(eq.office_validada) === 1;
const procesadorCorto = p => String(p || '').replace(/\(R\)|\(TM\)/g, '').replace(/\s+CPU\s*/i, ' ').replace(/\s{2,}/g, ' ').trim();
const discoCorto = raw => { const t = discosTexto(raw); return t ? t.split('-').pop().trim() : ''; };
const soCorto = eq => String(eq.windows || '').replace(/^Microsoft\s+/i, '');

const LOGO = '<svg class="logo" viewBox="0 0 64 64" xmlns="http://www.w3.org/2000/svg"><rect width="64" height="64" rx="14" fill="#08293f"/><path d="M15 47 27 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/><path d="M31 47 43 17" fill="none" stroke="#00b8e3" stroke-width="6" stroke-linecap="round"/></svg>';

const CSS = `
@page{size:A4;margin:12mm 13mm}
*{box-sizing:border-box;margin:0;padding:0;-webkit-print-color-adjust:exact;print-color-adjust:exact}
html,body{background:#fff}
body{font-family:'Segoe UI',Arial,sans-serif;color:#0b2538;font-size:11px;line-height:1.35}
table.pagina{width:100%;border-collapse:collapse}
table.pagina>thead{display:table-header-group}
table.pagina>tfoot{display:table-footer-group}
table.pagina>thead>tr>td,table.pagina>tbody>tr>td,table.pagina>tfoot>tr>td{padding:0;border:0;vertical-align:top}
.logo{width:36px;height:36px;flex:none}
.cab{display:flex;align-items:center;gap:12px;padding-bottom:8px;border-bottom:3px solid #00b8e3;margin-bottom:10px}
.cab .t{flex:1;min-width:0}
.marca{font-size:8.5px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#0087a8}
.cab h1{font-size:18px;line-height:1.15;color:#08293f;font-weight:800;margin-top:1px}
.cab h1 span{color:#0087a8}
.cab .meta{font-size:10px;color:#4a6577;margin-top:1px}
.cab .der{text-align:right;font-size:10px;color:#3d5668;line-height:1.4;max-width:46%}
.cab .der b{display:block;font-size:13px;color:#08293f}
.cab .der small{display:block;font-size:8px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#4a6577}
.pie{display:flex;justify-content:space-between;font-size:8.5px;color:#6b8191;border-top:1px solid #dbe6ee;padding-top:4px;margin-top:8px}
.kpis{display:grid;grid-template-columns:repeat(4,1fr);gap:8px;margin-bottom:10px}
.kpi{border:1px solid #cfdde8;border-radius:4px;padding:6px 10px;background:#f6f9fb}
.kpi small{display:block;font-size:8px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#4a6577}
.kpi b{display:block;font-size:14px;color:#08293f;margin-top:1px}
table.lista{width:100%;border-collapse:collapse;table-layout:fixed}
table.lista thead{display:table-header-group}
table.lista th{font-size:8px;font-weight:800;letter-spacing:.07em;text-transform:uppercase;color:#08293f;text-align:left;padding:6px 5px;background:#e8f1f7;border-bottom:2px solid #08293f}
table.lista td{padding:6px 5px;border-bottom:1px solid #dbe6ee;vertical-align:top;font-size:9px;overflow-wrap:anywhere}
table.lista tr{break-inside:avoid}
table.lista tbody tr:nth-child(even) td{background:#f7fafc}
td.n{font-weight:700;color:#08293f}
td.m{font-family:'Consolas','Courier New',monospace;font-size:9px;color:#3d5668}
td.r,th.r{text-align:right}
.dot{display:inline-block;width:8px;height:8px;border-radius:50%;margin-right:5px}
.dot-v{background:#16a34a}.dot-m{background:#d6247f}.dot-n{background:#b6c4cf}
.leyenda{margin-top:8px;font-size:9px;color:#4a6577}
.fichas-inicio{break-before:page}
.par{break-inside:avoid;break-after:page}
.par:last-child{break-after:auto}
.ficha{break-inside:avoid;border:1px solid #cfdde8;border-radius:5px;padding:10px 12px;margin-bottom:10px}
.par .ficha{height:116mm;overflow:hidden}
.fcab{display:flex;justify-content:space-between;align-items:flex-start;gap:12px;padding-bottom:7px;border-bottom:2px solid #00b8e3;margin-bottom:8px}
.fcab .k{font-size:8px;font-weight:700;letter-spacing:.16em;text-transform:uppercase;color:#0087a8}
.fcab h2{font-size:17px;color:#08293f;font-weight:800;line-height:1.15;margin-top:1px}
.fcab .resp{font-size:10.5px;color:#3d5668;margin-top:1px}
.est{flex:none;font-size:9px;font-weight:800;text-transform:uppercase;letter-spacing:.06em;padding:4px 10px;border-radius:4px;border:1.5px solid}
.est-v{color:#166534;border-color:#16a34a;background:#e9f8ef}
.est-m{color:#9d174d;border-color:#d6247f;background:#fdebf4}
.est-n{color:#475569;border-color:#b6c4cf;background:#f1f5f8}
.res{display:grid;grid-template-columns:repeat(4,1fr);gap:6px;margin-bottom:8px}
.res div{border:1px solid #cfdde8;border-radius:4px;padding:6px 8px;background:#f6f9fb;text-align:center}
.res small{display:block;font-size:7.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#4a6577}
.res b{display:block;font-size:11px;color:#08293f;margin-top:2px;word-break:break-word}
.g{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.c{border:1px solid #dbe6ee;border-radius:4px;padding:5px 8px;background:#fff;min-height:34px}
.c.d2{grid-column:span 2}.c.d3{grid-column:1/-1}
.c small{display:block;font-size:7.5px;font-weight:700;letter-spacing:.1em;text-transform:uppercase;color:#4a6577;margin-bottom:1px}
.c b{display:block;font-size:10.5px;color:#08293f;font-weight:700;word-break:break-word}
.c b.m{font-family:'Consolas','Courier New',monospace;font-size:9.5px}
.c b.v{color:#6b8191;font-weight:400;font-style:italic}
.solo .ficha{padding:14px 16px}
.solo .fcab h2{font-size:21px}
.solo .res b{font-size:12.5px}
.solo .c{min-height:42px}.solo .c b{font-size:12px}
.dot-p{background:#2f80d1}.dot-b{background:#9aa8b4}
.est-p{color:#1e5aa8;border-color:#64b5f6;background:#eaf3fd}
.est-b{color:#475569;border-color:#9aa8b4;background:#eceff2}
.cards{display:grid;grid-template-columns:1fr 1fr;gap:6px;align-items:start}
.card{border:1px solid #dbe6ee;border-radius:4px;padding:5px 8px;break-inside:avoid;background:#fff}
.card h3{font-size:8px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:#0087a8;margin-bottom:3px;padding-bottom:2px;border-bottom:1px solid #e3edf4}
.kv{display:grid;grid-template-columns:34% 1fr;gap:4px;padding:2px 0;border-top:1px solid #f0f5f9}
.card h3+.kv{border-top:0}
.kv small{font-size:7.5px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#4a6577;padding-top:1px}
.kv b{font-size:10px;font-weight:600;color:#08293f;word-break:break-word}
.kv b.m{font-family:'Consolas','Courier New',monospace;font-size:9px}
.kv b.v{color:#6b8191;font-weight:400;font-style:italic}
.chip{display:inline-block;font-size:8px;font-weight:700;padding:1px 6px;border-radius:9px;border:1px solid #cfdde8;background:#f1f5f8;color:#3d5668;margin-left:4px;vertical-align:1px}
.chip-ok{color:#166534;border-color:#16a34a;background:#e9f8ef}
.solo .kv b{font-size:11.5px}.solo .kv{padding:3px 0}
table.lista tr.area td{background:#e8f1f7;font-weight:800;font-size:8px;letter-spacing:.08em;text-transform:uppercase;color:#08293f;padding:4px 5px;border-bottom:1px solid #b9cdda}
table.lista tr.area+tr td{border-top:0}
.ok{color:#166534;font-weight:800}
.sec{margin-top:10px;break-inside:avoid;border:1px solid;border-radius:4px;overflow:hidden}
.sec-p{border-color:#64b5f6}.sec-b{border-color:#9aa8b4}
.sec h4{font-size:8px;font-weight:800;letter-spacing:.1em;text-transform:uppercase;padding:4px 8px}
.sec-p h4{background:#dbeafe;color:#1e5aa8}.sec-b h4{background:#e5e7eb;color:#4b5563}
.sec table{width:100%;border-collapse:collapse;table-layout:fixed}
.sec td{padding:4px 8px;font-size:9px;border-top:1px solid #e3edf4;vertical-align:top;overflow-wrap:anywhere}
.sec-b td{color:#6b7280}.sec-b td.n{text-decoration:line-through}
`;

const kv = (label, valor, { mono = false, vacio = '—', html = '' } = {}) => `<div class="kv"><small>${esc(label)}</small><b class="${mono ? 'm' : ''}${valor || html ? '' : ' v'}">${html || esc(valor || vacio)}</b></div>`;

function fichaHtml(eq, idx, total, empresa) {
  const est = estadoDe(eq);
  const so = [eq.windows, eq.windows_version].filter(Boolean).join(' — ');
  const ram = eq.memoria_ram_gb ? `${eq.memoria_ram_gb} GB` : '';
  const disco = discosTexto(eq.discos);
  const mant = fechaCorta(eq.ultimo_mantenimiento) || fechaCorta(eq.ultimo_evento_fecha);
  const val = dinero(eq.valor_comercial);
  const officeHtml = eq.office_version || eq.office_licencia
    ? `${esc(eq.office_version || '—')}${validada(eq) ? '<span class="chip chip-ok">\u2713 validada</span>' : ''}${eq.office_caja ? `<span class="chip">Caja ${esc(eq.office_caja)}</span>` : ''}`
    : '';
  const licenciaVacia = eq.office_serial ? `...${eq.office_serial} (clave completa pendiente)` : 'No registrada';
  return `
  <div class="ficha">
    <div class="fcab">
      <div>
        <div class="k">Equipo ${idx + 1} de ${total} · ${esc(eq.empresa_origen || eq.empresa || empresa)}</div>
        <h2>${esc(nombreEquipo(eq))}</h2>
        <div class="resp">Responsable: ${esc(eq.responsable || 'Sin asignar')}</div>
      </div>
      <span class="est est-${est.clave}">${est.texto}</span>
    </div>
    <div class="res">
      <div><small>Procesador</small><b style="font-size:9.5px">${esc(procesadorCorto(eq.procesador) || '—')}</b></div>
      <div><small>RAM</small><b>${esc(ram || '—')}</b></div>
      <div><small>Almacenamiento</small><b style="font-size:9.5px">${esc(discoCorto(eq.discos) || '—')}</b></div>
      <div><small>Valor comercial</small><b>${esc(val || '—')}</b></div>
    </div>
    <div class="cards">
      <div class="card"><h3>Usuario y contacto</h3>
        ${kv('Responsable', eq.responsable)}${kv('Área', eq.area)}${kv('Usuario Windows', eq.usuario_windows)}${kv('Correo', eq.correo_electronico)}
      </div>
      <div class="card"><h3>Software y Office</h3>
        ${kv('Sistema', so, { vacio: 'No registrado' })}${kv('Office', '', { html: officeHtml, vacio: 'No registrado' })}${kv('Licencia', eq.office_licencia, { mono: true, vacio: licenciaVacia })}
      </div>
      <div class="card"><h3>Hardware</h3>
        ${kv('Serial', eq.serial, { mono: true })}${kv('Disco duro', disco)}${kv('Video', eq.tarjeta_video)}${kv('MAC WiFi', eq.mac_wifi, { mono: true })}${kv('MAC Ethernet', eq.mac_ethernet, { mono: true })}
      </div>
      <div class="card"><h3>Mantenimiento</h3>
        ${kv('Último', mant, { vacio: 'No registrado' })}${kv('Valor comercial', val, { vacio: 'No registrado' })}${kv('Observaciones', eq.observaciones_entrega, { vacio: 'Sin notas' })}
      </div>
    </div>
  </div>`;
}

function officeCelda(eq) {
  if (eq.office_licencia) {
    return `${validada(eq) ? '<span class="ok">\u2713</span> ' : ''}${esc([eq.office_version, `...${ultimos5(eq.office_licencia)}`, cajaOffice(eq)].filter(Boolean).join(' · '))}`;
  }
  const parcial = [eq.office_version, eq.office_serial ? `...${eq.office_serial}` : ''].filter(Boolean).join(' · ');
  return parcial ? `<span style="color:#6b8191">${esc(parcial)}</span>` : '—';
}

function listadoHtml(rows) {
  const activos = rows.filter(e => !esEspecial(e));
  const pendientes = rows.filter(e => e.clasificacion_color === 'pendiente');
  const bajas = rows.filter(e => e.clasificacion_color === 'baja');
  const total = activos.length + pendientes.length;
  const verdes = activos.filter(e => e.clasificacion_color === 'verde').length;
  const magenta = activos.filter(e => e.clasificacion_color === 'magenta').length;
  const conOffice = [...activos, ...pendientes].filter(e => e.office_licencia);
  const validadas = conOffice.filter(validada).length;
  const valor = [...activos, ...pendientes].reduce((s, e) => s + (Number(e.valor_comercial) || 0), 0);
  const porArea = activos.reduce((acc, eq) => {
    const a = (eq.area || '').trim() || 'Sin área';
    (acc[a] = acc[a] || []).push(eq);
    return acc;
  }, {});
  const areas = Object.entries(porArea).sort((x, y) => (x[0] === 'Sin área') - (y[0] === 'Sin área') || x[0].localeCompare(y[0], 'es'));
  const fila = eq => {
    const est = estadoDe(eq);
    const hw = [procesadorCorto(eq.procesador), eq.memoria_ram_gb ? `${eq.memoria_ram_gb} GB` : '', discoCorto(eq.discos)].filter(Boolean).join(' · ');
    return `<tr>
      <td class="n"><span class="dot dot-${est.clave}"></span>${esc(nombreEquipo(eq))}</td>
      <td>${esc(eq.responsable || '—')}</td>
      <td>${esc(hw || '—')}</td>
      <td>${esc(soCorto(eq) || '—')}</td>
      <td class="m">${esc(eq.serial || '—')}</td>
      <td>${officeCelda(eq)}</td>
      <td class="r">${esc(dinero(eq.valor_comercial) || '—')}</td>
    </tr>`;
  };
  const seccion = (clase, titulo, items, col4) => items.length ? `
  <div class="sec sec-${clase}"><h4>${titulo} (${items.length})</h4>
    <table><colgroup><col style="width:22%"><col style="width:18%"><col style="width:20%"><col style="width:40%"></colgroup>
    ${items.map(eq => `<tr><td class="n">${esc(nombreEquipo(eq))}</td><td>${esc(eq.responsable || '—')}</td><td>${col4 === 'office' ? officeCelda(eq) : esc(eq.serial || '—')}</td><td>${esc(eq.observaciones_entrega || '—')}</td></tr>`).join('')}
    </table></div>` : '';
  return `
  <div class="kpis" style="grid-template-columns:repeat(5,1fr)">
    <div class="kpi"><small>Equipos</small><b>${total}</b></div>
    <div class="kpi"><small>Mantenimiento al día</small><b>${verdes}</b></div>
    <div class="kpi"><small>Críticos para cambio</small><b>${magenta}</b></div>
    <div class="kpi"><small>Office validado</small><b>${conOffice.length ? `${validadas}/${conOffice.length}` : '—'}</b></div>
    <div class="kpi"><small>Valor comercial</small><b>${valor ? esc(dinero(valor)) : '—'}</b></div>
  </div>
  <table class="lista">
    <colgroup><col style="width:17%"><col style="width:13%"><col style="width:22%"><col style="width:9%"><col style="width:11%"><col style="width:18%"><col style="width:10%"></colgroup>
    <thead><tr><th>Equipo</th><th>Responsable</th><th>Hardware</th><th>Sistema</th><th>Serial</th><th>Office</th><th class="r">Valor</th></tr></thead>
    <tbody>${areas.map(([area, items]) => `<tr class="area"><td colspan="7">${esc(area)} · ${items.length} equipo${items.length === 1 ? '' : 's'}</td></tr>${items.map(fila).join('')}`).join('')}</tbody>
  </table>
  ${seccion('p', 'Pendientes', pendientes, 'office')}${seccion('b', 'Dados de baja', bajas, 'serial')}
  <div class="leyenda"><span class="dot dot-v"></span>Mantenimiento al día &nbsp;&nbsp; <span class="dot dot-m"></span>Crítico para cambio &nbsp;&nbsp; <span class="dot dot-n"></span>Sin estado &nbsp;&nbsp; <span class="dot dot-p"></span>Pendiente &nbsp;&nbsp; <span class="dot dot-b"></span>Dado de baja</div>`;
}

function encabezadoHtml({ empresa, area, total, unico, infoEmpresa }) {
  if (unico) {
    const datos = [
      infoEmpresa?.nit ? `NIT ${esc(infoEmpresa.nit)}` : '',
      infoEmpresa?.direccion ? esc(infoEmpresa.direccion) : '',
      infoEmpresa?.correo ? esc(infoEmpresa.correo) : '',
    ].filter(Boolean).join('<br>');
    return `<div class="cab">${LOGO}
      <div class="t"><div class="marca">AGE Informática · Ficha técnica de equipo</div><h1>${esc(empresa)}</h1><div class="meta">Generado el ${esc(fechaLarga())}</div></div>
      <div class="der"><small>Empresa</small><b>${esc(empresa)}</b>${datos}</div></div>`;
  }
  return `<div class="cab">${LOGO}
    <div class="t"><div class="marca">AGE Informática · Inventario técnico</div>
      <h1>${esc(empresa)}${area ? ` <span>· Área ${esc(area)}</span>` : ''}</h1>
      <div class="meta">${total} equipo${total === 1 ? '' : 's'} · Generado el ${esc(fechaLarga())}</div></div>
    <div class="der"><small>${area ? 'Área' : 'Empresa'}</small><b>${esc(area || empresa)}</b></div></div>`;
}

export function construirHtmlInventario({ empresa, titulo, equipos, modo = 'completo', area = '', infoEmpresa = null }) {
  const rows = equipos || [];
  const unico = rows.length === 1 && modo === 'fichas';
  const verListado = modo === 'resumen' || modo === 'completo';
  const verFichas = modo === 'fichas' || modo === 'completo';
  const pares = [];
  if (verFichas) {
    for (let i = 0; i < rows.length; i += 2) {
      pares.push(`<div class="par">${fichaHtml(rows[i], i, rows.length, empresa)}${rows[i + 1] ? fichaHtml(rows[i + 1], i + 1, rows.length, empresa) : ''}</div>`);
    }
  }
  const cuerpo = (verListado ? listadoHtml(rows) : '')
    + (verFichas ? `<div class="${verListado ? 'fichas-inicio ' : ''}${unico ? 'solo' : ''}">${pares.join('')}</div>` : '');
  const pie = `<div class="pie"><span>AGE Informática · contable.ageinformatica.com</span><span>${esc(empresa)}${area ? ` · Área ${esc(area)}` : ''} · ${esc(fechaLarga())}</span></div>`;
  return `<!doctype html><html lang="es"><head><meta charset="UTF-8"><title>${esc(titulo)}</title><style>${CSS}</style></head><body>
<table class="pagina"><thead><tr><td>${encabezadoHtml({ empresa, area, total: rows.length, unico, infoEmpresa })}</td></tr></thead>
<tbody><tr><td>${cuerpo}</td></tr></tbody>
<tfoot><tr><td>${pie}</td></tr></tfoot></table>
<script>window.onload=()=>setTimeout(()=>window.print(),300)</script></body></html>`;
}
